import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  LOCALE_ID,
} from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { translocoProviders } from "./core/providers/transloco.providers";
import { JwtInterceptor } from "./core/interceptors/jwt.interceptor";
import {
  LocationStrategy,
  PathLocationStrategy,
  registerLocaleData,
} from "@angular/common";
import { provideRouter } from "@angular/router";
import { appRoutes } from "./app.routes";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialogModule } from "@angular/material/dialog";
import { MatCardModule } from "@angular/material/card";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatMenuModule } from "@angular/material/menu";
import { MatListModule } from "@angular/material/list";
import { ChangeTheme } from "./core/services/common/changeTheme.service";
import { DialogInitService } from "./core/services/common/dialog.service";
import { adapterFactory } from "angular-calendar/date-adapters/date-fns";
import {
  CalendarModule,
  DateAdapter as CalendarDateAdapter,
} from "angular-calendar";
import { DateAdapter as MatDateAdapter } from "@angular/material/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MatTableModule } from "@angular/material/table";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatIconModule } from "@angular/material/icon";
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import {
  MomentDateAdapter,
  MatMomentDateModule,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from "@angular/material-moment-adapter";
import localeEs from "@angular/common/locales/es";
import moment from "moment";
import "moment/locale/es";
import { httpErrorInterceptor } from "@core/interceptors/http-error.interceptor";
import { authRefreshInterceptor } from "@core/interceptors/auth-refresh.interceptor";
import { globalLoadingInterceptor } from "@core/interceptors/global-loading.interceptor";
import { ChunkErrorHandler } from "@core/services/common/chunk-error-handler.service";
import {
  hayLocksHuerfanos,
  LockWebService,
} from "@core/services/common/lock-web.service";
import { SesionActivaService } from "@core/services/common/sesion-activa.service";
import { SessionRefreshService } from "@core/services/common/session-refresh.service";
import { MatPaginationIntlService } from "@core/services/common/mat-paginator-translate";

// Configurar moment en español
moment.locale("es");

registerLocaleData(localeEs);

// Formato personalizado para fechas
const MY_DATE_FORMATS = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptorsFromDi(),
      // authRefreshInterceptor va ÚLTIMO a propósito: es el más cercano al backend, así que ve el
      // 401 antes que httpErrorInterceptor. Si se adelantara, cada vencimiento de access token
      // mostraría un snackbar rojo aunque el refresh lo resuelva sin que el usuario se entere.
      withInterceptors([
        globalLoadingInterceptor,
        httpErrorInterceptor,
        authRefreshInterceptor,
      ]),
    ),
    provideAnimations(),
    translocoProviders,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    {
      // Libera los locks que dejó la carga anterior de la app en esta pestaña (F5 con
      // un lock tomado). Bloquea el arranque a propósito: si el usuario volviera a
      // lockear el mismo registro antes de que salga este DELETE, la liberación tardía
      // borraría el lock nuevo.
      //
      // El arranque normal (sin huérfanos) cuesta una sola lectura de sessionStorage:
      // se resuelve con `hayLocksHuerfanos()` ANTES de tocar el injector, para no
      // construir LockWebService — ni HttpClient/MatSnackBar/TranslocoService detrás —
      // en el camino crítico del bootstrap. El servicio sigue siendo lazy como antes.
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const injector = inject(Injector);
        return () => {
          if (!hayLocksHuerfanos()) return;
          return injector.get(LockWebService).liberarHuerfanos();
        };
      },
    },
    {
      // Instancia el servicio de refresh para que el refresco preventivo quede programado desde
      // el arranque. Hace falta porque tras un F5 la sesión sigue viva en las cookies pero el
      // vencimiento solo está en sessionStorage: sin esto, nadie lo leería hasta el primer 401.
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const injector = inject(Injector);
        return () => {
          injector.get(SessionRefreshService);
        };
      },
    },
    {
      // Instancia el monitor de sesión: se queda dormido hasta que haya un lock activo, y
      // recién ahí engancha los listeners de actividad. Nadie más lo inyecta — vigila
      // `LockWebService.activeLocks`, no una pantalla puntual. No devuelve nada: no debe
      // bloquear el arranque.
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const injector = inject(Injector);
        return () => {
          injector.get(SesionActivaService);
        };
      },
    },
    {
      provide: MatDateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: "es" },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: LOCALE_ID, useValue: "es" },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    provideRouter(appRoutes),
    importProvidersFrom(
      MatSnackBarModule,
      MatDialogModule,
      MatCardModule,
      MatDatepickerModule,
      MatSidenavModule,
      MatMenuModule,
      MatIconModule,
      MatListModule,
      MatButtonToggleModule,
      MatTableModule,
      MatMomentDateModule,
      CalendarModule.forRoot({
        provide: CalendarDateAdapter,
        useFactory: adapterFactory,
      }),
    ),
    // Traduce los textos del paginador de TODAS las grillas. Va al final a propósito:
    // MatPaginatorModule arrastra MAT_PAGINATOR_INTL_PROVIDER, que en el root injector no
    // tiene padre del que heredar y devuelve el MatPaginatorIntl default, en inglés. Si se
    // declara antes de un importProvidersFrom de ese módulo, gana el default.
    { provide: MatPaginatorIntl, useClass: MatPaginationIntlService },
    DialogInitService,
    ChangeTheme,
  ],
};
